import { useReveal } from '../hooks/useReveal.js';

type Icon = {
  slug: string;
  name: string;
  attribution: string;
  alt: string;
};

// Confident identifications only - these are sacred images and the audience
// is reverent (see brand voice). Feast icons are shown elsewhere; here we keep
// the iconostasis to Christ, the Theotokos, the angels, and named saints.
const icons: Icon[] = [
  // The Theotokos stands to the viewer's left of Christ, as on a real
  // iconostasis.
  {
    slug: 'theotokos',
    name: 'The Theotokos',
    attribution: 'Mother of God',
    alt: 'Icon of the Most Holy Theotokos and the Christ Child',
  },
  {
    slug: 'pantocrator',
    name: 'Christ Pantocrator',
    attribution: 'Ruler of All',
    alt: 'Icon of Christ Pantocrator holding the Gospel',
  },
  {
    slug: 'archangel-michael',
    name: 'Archangel Michael',
    attribution: 'Captain of the Heavenly Host',
    alt: 'Icon of the Holy Archangel Michael',
  },
  {
    slug: 'archangel-gabriel',
    name: 'Archangel Gabriel',
    attribution: 'Herald of the Annunciation',
    alt: 'Icon of the Holy Archangel Gabriel',
  },
  {
    slug: 'george',
    name: 'St George',
    attribution: 'The Trophy-Bearer',
    alt: 'Icon of Saint George the Trophy-Bearer',
  },
  {
    slug: 'nicholas',
    name: 'St Nicholas',
    attribution: 'Wonderworker of Myra',
    alt: 'Icon of Saint Nicholas of Myra',
  },
  {
    slug: 'john-climacus',
    name: 'St John of the Ladder',
    attribution: 'Author of the Ladder',
    alt: 'Icon of Saint John Climacus holding the Ladder of Divine Ascent',
  },
  {
    slug: 'all-saints',
    name: 'All Saints',
    attribution: 'The Great Cloud of Witnesses',
    alt: 'Icon of All Saints gathered around Christ',
  },
];

function IconCard({ icon, delay }: { icon: Icon; delay: number }) {
  const ref = useReveal<HTMLAnchorElement>(0.25);
  const base = `/icons/saint-${icon.slug}-sq`;
  return (
    <a
      ref={ref}
      href="#manifesto"
      aria-label={icon.name}
      className="ow-reveal ow-icon-link ow-focusable flex flex-col items-center text-center"
      style={
        {
          ['--reveal-delay' as never]: `${delay}ms`,
        } as React.CSSProperties
      }
    >
      <div
        className="ow-icon-frame relative"
        style={{ width: 'min(220px, 40vw)', aspectRatio: '1' }}
      >
        {/* gold halo behind */}
        <span
          aria-hidden
          className="ow-icon-halo absolute inset-[-12%] rounded-full"
          style={{
            background: 'radial-gradient(closest-side, var(--ow-gold-glow), transparent 70%)',
          }}
        />
        <div
          className="absolute inset-0 rounded-full overflow-hidden border border-gold"
          style={{ boxShadow: 'var(--ow-shadow-md)' }}
        >
          <picture>
            <source srcSet={`${base}.webp`} type="image/webp" />
            <img
              src={`${base}.jpg`}
              alt={icon.alt}
              loading="lazy"
              decoding="async"
              width={520}
              height={520}
              className="ow-icon-img w-full h-full object-cover"
            />
          </picture>
        </div>
      </div>

      <p
        className="font-display text-primary mt-6"
        style={{
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: '0.04em',
        }}
      >
        {icon.name}
      </p>
      <p
        className="text-secondary mt-1"
        style={{ fontSize: 12, lineHeight: 1.6, letterSpacing: '0.02em' }}
      >
        {icon.attribution}
      </p>
    </a>
  );
}

export function Iconostasis() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  return (
    <section
      id="iconostasis"
      className="relative w-full"
      style={{ padding: '76px 20px', background: 'var(--ow-bg-subtle)' }}
    >
      <div className="mx-auto max-w-container">
        <div ref={headerRef} className="ow-reveal text-center max-w-[760px] mx-auto">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            Under their intercession
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            The Iconostasis
          </h2>
          <p className="scripture text-secondary mt-4" style={{ fontSize: 19, lineHeight: 1.55 }}>
            “Most holy Theotokos, save us. Holy Archangels, pray for us.”
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14 mt-16">
          {icons.map((ic, i) => (
            <IconCard key={ic.slug} icon={ic} delay={(i % 4) * 120} />
          ))}
        </div>
      </div>
    </section>
  );
}
